import React, { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Loader2, Eye, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DdApiKeysPanel } from "./DdApiKeysPanel";

const today = () => new Date().toISOString().slice(0, 10);
const errText = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fb;
};

const PreviewTable = ({ preview }) => (
  <div className="border border-gray-200 rounded-md overflow-x-auto" data-testid="dd-preview-table">
    <table className="w-full text-[12px] whitespace-nowrap">
      <thead className="bg-[#16345c] text-white">
        <tr>{preview.header.map((h) => <th key={h} className="px-2.5 py-2 text-left font-bold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {preview.rows.map((r, i) => (
          <tr key={i} className="border-t border-gray-100 odd:bg-gray-50/60" data-testid={`dd-preview-row-${i}`}>
            {r.map((c, j) => <td key={j} className="px-2.5 py-1.5 text-gray-700">{c}</td>)}
          </tr>
        ))}
        {preview.rows.length === 0 && (
          <tr><td colSpan={preview.header.length} className="px-3 py-6 text-center text-gray-400">Nessun rapporto attivo alla data di riferimento.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

// Export Due Diligence: data di riferimento → anteprima prime 5 righe → download CSV.
export const DueDiligenceDialog = ({ open, onClose, client, basePath, companyLabel, keysPath, apiCompany }) => {
  const [date, setDate] = useState(today());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadPreview = async () => {
    if (!date) { toast.error("Indica la data di riferimento"); return; }
    setLoading(true);
    try {
      const r = await client.get(`${basePath}/preview`, { params: { data: date } });
      setPreview(r.data);
    } catch (e) { setPreview(null); toast.error(errText(e, "Anteprima non disponibile")); }
    finally { setLoading(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const r = await client.get(`${basePath}/export`, { params: { data: preview.date }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = preview.filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Scaricato ${preview.filename}`);
    } catch (e) { toast.error("Download non riuscito"); }
    finally { setDownloading(false); }
  };

  const close = () => { setPreview(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-[860px] max-h-[90vh] overflow-y-auto" data-testid="dd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet size={18} className="text-[#16345c]" /> Esporta per Due Diligence</DialogTitle>
          <DialogDescription className="text-[13px] text-gray-600">
            Organico {companyLabel ? <b>{companyLabel}</b> : "dell'azienda"} in CSV (separatore ";", UTF-8). Ferie, permessi e TFR sono calcolati alla data di riferimento; sono inclusi solo i rapporti attivi a quella data.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3 flex-wrap">
          <label className="block">
            <span className="block text-[12px] font-semibold text-gray-600 mb-1">Data di riferimento *</span>
            <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPreview(null); }} data-testid="dd-ref-date"
              className="border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]" />
          </label>
          <button onClick={loadPreview} disabled={loading} data-testid="dd-preview-btn"
            className="flex items-center gap-2 bg-white text-[#16345c] border border-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Mostra anteprima
          </button>
          <button onClick={download} disabled={!preview || downloading} data-testid="dd-download-btn"
            className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors disabled:opacity-40">
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Scarica CSV
          </button>
        </div>

        {preview && (
          <div className="mt-2 space-y-2">
            <div className="text-[12.5px] text-gray-600" data-testid="dd-preview-summary">
              Anteprima delle prime {Math.min(5, preview.total)} righe su <b className="text-[#16345c]">{preview.total}</b> · file <span className="font-mono text-[12px]">{preview.filename}</span>
            </div>
            <PreviewTable preview={preview} />
            {preview.inferred_type > 0 && (
              <p className="flex items-start gap-1.5 text-[12px] text-amber-700" data-testid="dd-inferred-note">
                <Info size={14} className="mt-0.5 shrink-0" />
                {preview.inferred_type} {preview.inferred_type === 1 ? "rapporto non ha" : "rapporti non hanno"} il "Tipo rapporto" in scheda: dedotto da fine contratto (td) o, in assenza, tempo indeterminato (ti). Completa la scheda per un dato certo.
              </p>
            )}
          </div>
        )}

        {keysPath && <DdApiKeysPanel client={client} keysPath={keysPath} company={apiCompany} />}
      </DialogContent>
    </Dialog>
  );
};

export default DueDiligenceDialog;
